import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';

const VariantPricingModal = ({ isOpen, onClose, product, variants, onSuccess }) => {
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && variants) {
      // Group variants by RAM+ROM
      const grouped = {};
      
      variants.forEach(variant => {
        const key = `${variant.storage}-${variant.ram || 'N/A'}`;
        if (!grouped[key]) {
          grouped[key] = {
            storage: variant.storage,
            ram: variant.ram,
            count: 0,
            mrp: variant.mrp || '',
            fsp: variant.fsp || '',
            hasPrice: !!(variant.mrp && variant.fsp)
          };
        }
        grouped[key].count++;
      });

      // Convert to array
      const combos = Object.values(grouped);
      setPricingData(combos);
    }
  }, [isOpen, variants]);

  const handlePriceChange = (index, field, value) => {
    const updated = [...pricingData];
    updated[index][field] = value;
    setPricingData(updated);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate all prices are entered
      const incomplete = pricingData.filter(item => !item.mrp || !item.fsp);
      if (incomplete.length > 0) {
        setError(`Please enter prices for all ${incomplete.length} combination(s)`);
        setLoading(false);
        return;
      }

      // Prepare bulk update data
      const updates = pricingData.map(item => ({
        storage: item.storage,
        ram: item.ram,
        mrp: parseInt(item.mrp),
        fsp: parseInt(item.fsp)
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/variants/pricing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ pricing_updates: updates })
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`✓ Pricing updated successfully!\n${data.updated_count} variants updated.`);
        onSuccess();
        onClose();
      } else {
        setError(data.detail || 'Failed to update pricing');
      }
    } catch (err) {
      console.error('Error updating pricing:', err);
      setError('Failed to update pricing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-primary-foreground flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Set Pricing for {product?.model_name}
            </h3>
            <button
              onClick={onClose}
              className="text-primary-foreground hover:text-primary-foreground/80"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* Info Message */}
            <div className="bg-info/10 border border-info/20 p-3 rounded mb-4">
              <p className="text-sm text-info">
                <strong>Note:</strong> Enter MRP and FSP for each RAM+ROM combination. Prices will be applied to all colors of that combination automatically.
              </p>
            </div>

            {/* Pricing Table */}
            <div className="space-y-4">
              {pricingData.map((combo, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${
                    combo.hasPrice ? 'bg-success/10 border-success/20' : 'bg-white border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {combo.storage} / {combo.ram || 'N/A'}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {combo.count} color{combo.count > 1 ? 's' : ''} • {product?.brand_name} {product?.model_name}
                      </p>
                    </div>
                    {combo.hasPrice && (
                      <span className="text-xs bg-success/10 text-success px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Price Set
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        MRP (₹)
                      </label>
                      <input
                        type="number"
                        value={combo.mrp}
                        onChange={(e) => handlePriceChange(index, 'mrp', e.target.value)}
                        placeholder="e.g., 119900"
                        className="w-full px-3 py-2 text-sm border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Website Selling Rate / FSP (₹)
                      </label>
                      <input
                        type="number"
                        value={combo.fsp}
                        onChange={(e) => handlePriceChange(index, 'fsp', e.target.value)}
                        placeholder="e.g., 89999"
                        className="w-full px-3 py-2 text-sm font-semibold text-emerald-700 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-danger/10 border border-danger/20 text-danger px-4 py-3 rounded flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-success text-base font-medium text-success-foreground hover:bg-success/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Pricing'}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-white text-base font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
            >
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantPricingModal;
